import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import styles from "./Main.module.css";

import { api } from "../../utils/services/api";

import Sidebar from "../../components/Sidebar";

import { ReactComponent as GeotechnicsIcon } from "./utils/img/drilling-rig.svg";
import { ReactComponent as StructureIcon } from "./utils/img/building.svg";

import { FaRegBuilding } from "react-icons/fa";
import { FaClipboardList } from "react-icons/fa";
import { IoIosSettings } from "react-icons/io";

import Project from "./pages/Project";
import Geotechnics from "./pages/Geotechnics";
import Solution from "./pages/Solution";
import Config from "./pages/Config";

import parametersSettings from "./utils/data/parametersSettings.json";
import dataGeotechnics from "./utils/data/dataGeotechnics.json";
import { useToast } from "@chakra-ui/react";

const initialGeotechnicsInputs = {
  method: "Aoki-Velloso",
  type: "Escavada",
  stress: "compressao",
  dimension_1: "",
  dimension_2: "",
  dimension_3: "0",
  dimension_4: "0",
};

function Main({ userInputs }) {
  const toast = useToast();

  // PROJECTS AND SETTINGS
  const [projectInputs, setProjectInputs] = useState({
    selected_name: "",
    name_input: "",
  });
  const [fundarsData, setFundarsData] = useState({
    projects: [],
    settings: [...parametersSettings["settings"]],
  });
  const [filteredProjects, setFilteredProjects] = useState([]);

  function updateFundarsData(key, value) {
    setFundarsData((prevInputs) => ({ ...prevInputs, [key]: value }));
  }

  function updateProjectInputs(key, value) {
    setProjectInputs((prevInputs) => ({ ...prevInputs, [key]: value }));
  }

  useEffect(() => {
    const fetchFundars = async () => {
      const toastId = toast({
        title: "Carregando projetos...",
        status: "loading",
        duration: null,
      });
      api
        .post("/project", userInputs)
        .then((response) => {
          const projects = [];
          response["data"].map((project) => {
            projects.push(project["name"]);
          });
          updateFundarsData("projects", projects);
          setFilteredProjects(projects);
        })
        .catch((error) => {
          toast({
            title: "Erro ao carregar os projetos",
            description: "Por favor, tente novamente mais tarde.",
            status: "error",
            isClosable: true,
          });
        })
        .finally(() => {
          toast.close(toastId);
        });
      api
        .post("/settings", userInputs)
        .then((response) => {
          if (response["data"].length > 0) {
            const all_settings = [
              ...parametersSettings["settings"],
              ...response["data"],
            ];
            updateFundarsData("settings", all_settings);
          }
        })
        .catch((error) => {
          toast({
            title: "Erro ao carregar as configurações",
            description: "Por favor, tente novamente mais tarde.",
            status: "error",
            isClosable: true,
          });
        });
    };
    fetchFundars();
  }, []);

  // GEOTECHNICS AND SOLUTION
  const [investigationInputs, setInvestigationInputs] = useState({
    selected_name: "",
    name_input: "",
  });
  const [geotechnicsInputs, setGeotechnicsInputs] = useState(
    initialGeotechnicsInputs
  );
  const [columnGroupInputs, setColumnGroupInputs] = useState({
    selected_name: "",
    name_input: "",
  });
  const [columnInputs, setColumnInputs] = useState({
    selected_name: "",
    name: "",
    combs: {
      fz_max: { fz: 0, fx: 0, fy: 0, mx: 0, my: 0 },
      fz_min: { fz: 0, fx: 0, fy: 0, mx: 0, my: 0 },
      mx_max: { fz: 0, fx: 0, fy: 0, mx: 0, my: 0 },
      my_max: { fz: 0, fx: 0, fy: 0, mx: 0, my: 0 },
    },
  });
  const [solutionInputs, setSolutionInputs] = useState({
    selected_name: "",
    name_input: "",
  });
  const [projectData, setProjectData] = useState({
    name: "",
    investigations: [],
    columns: [],
    solutions: [],
  });
  const [foundationClass, setFoundationClass] = useState("estacas");

  function updateInvestigationInputs(key, value) {
    setInvestigationInputs((prevInputs) => ({ ...prevInputs, [key]: value }));
  }

  function onClearGeotechnicsInputs() {
    setGeotechnicsInputs(initialGeotechnicsInputs);
  }

  function onClearGeotechnics() {
    projectData["investigations"][investigationInputs["selected_name"]].map(
      (layer, i) => {
        layer["rl"] = "";
        layer["rl_accumulated"] = "";
        layer["rp"] = "";
        layer["ru"] = "";
        layer["pa"] = "";
      }
    );
  }

  function updateGeotechnicsInputs(key, value) {
    setGeotechnicsInputs((prevInputs) => ({ ...prevInputs, [key]: value }));
    onClearGeotechnics();
  }

  function updateColumnGroupInputs(name, value) {
    setColumnGroupInputs((prevInputs) => ({
      ...prevInputs,
      [name]: value,
    }));
  }

  function updateColumnInputs(name, value) {
    setColumnInputs((prevInputs) => ({
      ...prevInputs,
      [name]: value,
    }));
  }

  function updateColumnInputsCombs(combName, key, value) {
    if (
      value === "" ||
      value === "-" ||
      value.endsWith(".") ||
      value.endsWith(".0") ||
      value.endsWith("-0")
    ) {
      setColumnInputs((prevInputs) => ({
        ...prevInputs,
        combs: {
          ...prevInputs.combs,
          [combName]: {
            ...prevInputs.combs?.[combName],
            [key]: value,
          },
        },
      }));
    } else {
      setColumnInputs((prevInputs) => ({
        ...prevInputs,
        combs: {
          ...prevInputs.combs,
          [combName]: {
            ...prevInputs.combs?.[combName],
            [key]: Number(value),
          },
        },
      }));
    }
  }

  function updateSolutionInputs(name, value) {
    setSolutionInputs((prevInputs) => ({
      ...prevInputs,
      [name]: value,
    }));
  }

  function updateProjectData(key, value) {
    setProjectData((prevInputs) => ({ ...prevInputs, [key]: value }));
  }

  useEffect(() => {
    if (projectInputs["selected_name"] !== "") {
      const fetchProject = async () => {
        const toastId = toast({
          title: "Carregando projeto...",
          status: "loading",
          duration: null,
        });
        api
          .post("/investigation", [userInputs, projectData["name"]])
          .then((response) => {
            Object.entries(response["data"]).map(([investigation, layers]) => {
              layers.map((layer, i) => {
                response["data"][investigation][i] = Object.assign(
                  layer,
                  dataGeotechnics[foundationClass][0]
                );
              });
            });
            updateProjectData("investigations", response["data"]);
            const sortedInvestigations = Object.keys(response["data"]).sort(
              (a, b) => a.localeCompare(b)
            );
            updateInvestigationInputs("selected_name", sortedInvestigations[0]);
          })
          .catch((error) => {
            toast({
              title: "Erro ao carregar projeto",
              description: "Por favor, tente novamente mais tarde.",
              status: "error",
              isClosable: true,
            });
          })
          .finally(() => {
            toast.close(toastId);
          });
        api
          .post("/column-group", [userInputs, projectData["name"]])
          .then((response) => {
            updateProjectData("columns", response["data"]);
            const sortedColumnsGroup = Object.keys(response["data"]).sort(
              (a, b) => a.localeCompare(b)
            );
            updateColumnGroupInputs("selected_name", sortedColumnsGroup[0]);
            if (response["data"][sortedColumnsGroup[0]].length > 0) {
              const sortedColumns = response["data"][
                sortedColumnsGroup[0]
              ].sort((a, b) => a["name"].localeCompare(b["name"]));
              updateColumnInputs("selected_name", sortedColumns[0]["name"]);
              updateColumnInputs("combs", sortedColumns[0]["combs"]);
            }
          })
          .catch((error) => {
            toast({
              title: "Erro ao carregar os setores do projeto",
              description: "Por favor, tente novamente mais tarde.",
              status: "error",
              isClosable: true,
            });
          });
        api
          .post("/solution", [userInputs, projectData["name"]])
          .then((response) => {
            updateProjectData("solutions", response["data"]);
            const sortedSolutions = Object.keys(response["data"]).sort((a, b) =>
              a.localeCompare(b)
            );
            updateSolutionInputs("selected_name", sortedSolutions[0]);
          })
          .catch((error) => {
            toast({
              title: "Erro ao carregar as fundações do projeto",
              description: "Por favor, tente novamente mais tarde.",
              status: "error",
              isClosable: true,
            });
          });
      };
      fetchProject();
    }
  }, [projectData["name"]]);

  // MAIN
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState("Projetos");
  const pages = {
    Projetos: {
      icon: <FaRegBuilding size="35px" />,
      action: () => {
        updateProjectInputs("selected_name", "");
        updateInvestigationInputs("selected_name", "");
        setProjectData({
          name: "",
          investigations: [],
          columns: [],
          solutions: [],
        });
        setSelectedOption("Projetos");
      },
      enabled: true,
      condition: true,
      component: (
        <Project
          userInputs={userInputs}
          projectInputs={projectInputs}
          updateProjectInputs={updateProjectInputs}
          fundarsData={fundarsData}
          updateFundarsData={updateFundarsData}
          filteredProjects={filteredProjects}
          setFilteredProjects={setFilteredProjects}
          updateProjectData={updateProjectData}
          setSelectedOption={setSelectedOption}
          onClearGeotechnicsInputs={onClearGeotechnicsInputs}
        />
      ),
    },
    Geotecnia: {
      icon: <GeotechnicsIcon width="40px" />,
      action: () => {
        onClearGeotechnics();
        updateGeotechnicsInputs("type", "Escavada");
        setSelectedOption("Geotecnia");
      },
      enabled: false,
      condition: selectedOption === "Geotecnia" || selectedOption === "Solução",
      component: (
        <Geotechnics
          userInputs={userInputs}
          fundarsData={fundarsData}
          investigationInputs={investigationInputs}
          updateInvestigationInputs={updateInvestigationInputs}
          geotechnicsInputs={geotechnicsInputs}
          updateGeotechnicsInputs={updateGeotechnicsInputs}
          projectData={projectData}
          updateProjectData={updateProjectData}
          foundationClass={foundationClass}
          setFoundationClass={setFoundationClass}
          onClearGeotechnics={onClearGeotechnics}
        />
      ),
    },
    Solução: {
      icon: <StructureIcon width="40px" />,
      action: () => setSelectedOption("Solução"),
      enable: false,
      condition:
        (selectedOption === "Geotecnia" &&
          projectData["investigations"][investigationInputs["selected_name"]] &&
          projectData["investigations"][investigationInputs["selected_name"]]
            .length > 0 &&
          projectData["investigations"][
            investigationInputs["selected_name"]
          ][0]["pa"] !== "") ||
        selectedOption === "Solução",
      component: (
        <Solution
          userInputs={userInputs}
          projectInputs={projectInputs}
          investigationInputs={investigationInputs}
          geotechnicsInputs={geotechnicsInputs}
          projectData={projectData}
          updateProjectData={updateProjectData}
          foundationClass={foundationClass}
          columnGroupInputs={columnGroupInputs}
          updateColumnGroupInputs={updateColumnGroupInputs}
          columnInputs={columnInputs}
          setColumnInputs={setColumnInputs}
          updateColumnInputs={updateColumnInputs}
          updateColumnInputsCombs={updateColumnInputsCombs}
          solutionInputs={solutionInputs}
          updateSolutionInputs={updateSolutionInputs}
        />
      ),
    },
    Configurações: {
      icon: <IoIosSettings size="35px" />,
      action: () => setSelectedOption("Configurações"),
      enable: true,
      condition: true,
      component: (
        <Config
          userInputs={userInputs}
          fundarsData={fundarsData}
          updateFundarsData={updateFundarsData}
        />
      ),
    },
  };

  return userInputs["id"] !== null ? (
    <div className={styles.bodyFundars}>
      <Sidebar
        product={"fundars"}
        plan={userInputs["licenses"]["fundars"]["plan"]}
        pages={pages}
        selected={selectedOption}
      />
      {pages[selectedOption]["component"]}
    </div>
  ) : (
    navigate("/")
  );
}

export default Main;
